import React from 'react';
import { Play } from 'lucide-react';

const HEADING_FONT = 'Manrope, sans-serif';

/**
 * Video Embed Block Renderer
 * Variants: youtube, vimeo, custom
 */
export default function VideoRenderer({ props, variant, isEditing }) {
  const {
    headline = '',
    video_url = '',
    thumbnail_url = '',
    autoplay = false,
    aspect_ratio = '16:9',
    background_color = '#ffffff',
  } = props;

  const aspectClass = {
    '16:9': 'aspect-video',
    '4:3': 'aspect-[4/3]',
    '1:1': 'aspect-square',
  }[aspect_ratio] || 'aspect-video';

  const headerEl = headline ? (
    <div className="text-center mb-8">
      <h2
        className="text-3xl sm:text-4xl font-bold text-stone-900 mb-3"
        style={{ fontFamily: HEADING_FONT }}
      >
        {headline}
      </h2>
    </div>
  ) : null;

  const renderEmbed = () => {
    // In edit mode, show thumbnail with play overlay instead of live embed
    if (isEditing) {
      return (
        <div className={`relative ${aspectClass} bg-stone-900 rounded-xl overflow-hidden`}>
          {thumbnail_url ? (
            <img
              src={thumbnail_url}
              alt="Video thumbnail"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-stone-800 flex items-center justify-center">
              <span className="text-stone-400 text-sm">
                {video_url || 'No video URL set'}
              </span>
            </div>
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
              <Play className="w-7 h-7 text-stone-900 ml-1" />
            </div>
          </div>
        </div>
      );
    }

    if (variant === 'youtube' || (!variant && video_url.includes('youtu'))) {
      const videoId = extractYouTubeId(video_url);
      if (!videoId) return <PlaceholderEmbed aspectClass={aspectClass} />;
      return (
        <div className={`relative ${aspectClass} rounded-xl overflow-hidden`}>
          <iframe
            src={`https://www.youtube.com/embed/${videoId}${autoplay ? '?autoplay=1&mute=1' : ''}`}
            title="YouTube video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        </div>
      );
    }

    if (variant === 'vimeo' || (!variant && video_url.includes('vimeo'))) {
      const videoId = extractVimeoId(video_url);
      if (!videoId) return <PlaceholderEmbed aspectClass={aspectClass} />;
      return (
        <div className={`relative ${aspectClass} rounded-xl overflow-hidden`}>
          <iframe
            src={`https://player.vimeo.com/video/${videoId}${autoplay ? '?autoplay=1&muted=1' : ''}`}
            title="Vimeo video"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        </div>
      );
    }

    // Custom / direct video URL
    if (video_url) {
      return (
        <div className={`relative ${aspectClass} rounded-xl overflow-hidden bg-black`}>
          <video
            src={video_url}
            poster={thumbnail_url}
            controls
            autoPlay={autoplay}
            muted={autoplay}
            className="absolute inset-0 w-full h-full object-contain"
          />
        </div>
      );
    }

    return <PlaceholderEmbed aspectClass={aspectClass} />;
  };

  return (
    <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
      <div className="max-w-4xl mx-auto">
        {headerEl}
        {renderEmbed()}
      </div>
    </section>
  );
}

function PlaceholderEmbed({ aspectClass }) {
  return (
    <div className={`relative ${aspectClass} bg-stone-100 rounded-xl flex items-center justify-center`}>
      <div className="text-center">
        <Play className="w-12 h-12 text-stone-300 mx-auto mb-2" />
        <p className="text-sm text-stone-400">Add a video URL to preview</p>
      </div>
    </div>
  );
}

function extractYouTubeId(url) {
  if (!url) return null;
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return match ? match[1] : null;
}

function extractVimeoId(url) {
  if (!url) return null;
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match ? match[1] : null;
}
